import { Injectable, inject, DestroyRef } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of, switchMap, tap, map } from 'rxjs';
import { TokenService } from '@aeCore/services/token.service';
import { catchError, take } from 'rxjs/operators';
import { UserService } from '@aeCore/services/user.service';
import { EUserType } from '@aeCore/enums/user-role.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private tokenService = inject(TokenService);
  private userService = inject(UserService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  private userInfo: any | null = null;

  canActivate(): Observable<boolean | UrlTree> {
    if (this.tokenService.isAccessTokenExpired()) {
      const refreshToken = this.tokenService.getRefreshToken();
      if (refreshToken) {
        return this.tokenService.refreshToken().pipe(
          switchMap(() => this.userService.getUserInfo()),
          switchMap(() => of(true)),
          catchError(() => {
            this.tokenService.removeTokens();
            return of(this.router.createUrlTree(['/auth']));
          }),
        );
      } else {
        this.tokenService.removeTokens();
        return of(this.router.createUrlTree(['/auth']));
      }
    } else {
      if (this.userInfo) {
        return of(this.checkTfa(this.userInfo));
      } else {
        return this.userService.getUserInfo().pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          tap(user => (this.userInfo = user)),
          map(user => this.checkTfa(user)),
        );
      }
    }
  }

  private checkTfa(user: any): boolean | UrlTree {
    const requiresTfa =
      user?.type === EUserType.ADMIN || user?.type === EUserType.SUPER_ADMIN;
    if (!requiresTfa) {
      return true;
    }

    if (!this.tokenService.isTfaCompleted()) {
      return this.router.createUrlTree(['/auth/tfa-setup']);
    }
    return true;
  }
}
